import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@core/services/api/apiRequest";
import type { PublicProfile, PublicProfileQuizResponse } from "../publicProfile.types";
import type { UserOrganizationAffiliation } from "@core/features/affiliations";

/**
 * API response from GET /api/profile/me/?program_id=<program_id>
 */
type OwnProfileApiResponse = {
  user_info: {
    id: number;
    first_name: string;
    last_name: string;
    profile_photo: string | null;
    pronouns: string | null;
    bio: string | null;
    website: string | null;
  };
  affiliations: UserOrganizationAffiliation[];
  profile_quiz_responses: PublicProfileQuizResponse[];
};

/**
 * Arguments for useFetchOwnPublicProfile hook.
 */
export type UseFetchOwnPublicProfileArgs = {
  /** Program ID to scope the profile to */
  programId: number | undefined;
  /** Whether the query is enabled */
  isEnabled?: boolean;
  /** Current user (to determine if logged in) */
  user: { id: number } | null;
};

/**
 * Transform API response to PublicProfile type.
 * Takes the first profile quiz response if multiple exist.
 */
function transformToPublicProfile(data: OwnProfileApiResponse): PublicProfile {
  return {
    user: {
      id: data.user_info.id,
      first_name: data.user_info.first_name,
      last_name: data.user_info.last_name,
      profile_photo: data.user_info.profile_photo,
      pronouns: data.user_info.pronouns,
      bio: data.user_info.bio,
      website: data.user_info.website,
    },
    // Use the first profile quiz response if available
    quiz_response: data.profile_quiz_responses.length > 0
      ? data.profile_quiz_responses[0]
      : null,
    affiliations: data.affiliations,
  };
}

/**
 * Hook to fetch the current user's own public profile.
 * Returns React Query result with data transformed to PublicProfile type.
 *
 * @example
 * ```typescript
 * const { data: profile, isLoading, error } = useFetchOwnPublicProfile({
 *   programId: 123,
 *   user,
 * });
 *
 * if (isLoading) return <Loader />;
 * if (!profile) return null;
 *
 * return <PublicProfileSheet profile={profile} />;
 * ```
 */
export function useFetchOwnPublicProfile({
  programId,
  isEnabled = true,
  user,
}: UseFetchOwnPublicProfileArgs) {
  return useQuery<PublicProfile, Error>({
    queryKey: ["own-public-profile", programId],
    queryFn: async () => {
      const response = (await apiRequest(
        `/profile/me/?program_id=${programId}`,
        "GET"
      )) as OwnProfileApiResponse;
      return transformToPublicProfile(response);
    },
    enabled: !!user && !!programId && isEnabled,
  });
}

export default useFetchOwnPublicProfile;
